export const DEFAULT_UPGRADE_URL = 'https://eauditor.app/pricing/'

/**
 * Free-plan usage UI: header chip, template/user strips, limit banner, and block dialog.
 * Temporarily off. Set true again, with the two flags below, to show the license pages.
 */
export const ENABLE_FREE_PLAN_USAGE_UI = false

/**
 * Account & Plan billing. Upgrade opens the in-app license shopping cart.
 * Temporarily off. Set true again, with the flags around it, to show the license pages.
 */
export const ENABLE_SERVICE_PLAN_STRIPE_FLOW = false

/**
 * In-app license shopping cart (plan, cart, payment, confirmation).
 * Temporarily off. Set true to show the checkout route and upgrade buttons again.
 * No cart code is removed.
 */
export const ENABLE_LICENSE_SHOPPING_CART = false

/** Settings → Account & Plan tab + route. Off while both Free UI and Stripe are off. */
export const ENABLE_ACCOUNT_PLAN_PAGE =
    ENABLE_FREE_PLAN_USAGE_UI || ENABLE_SERVICE_PLAN_STRIPE_FLOW

/** Placeholder from mockup/API that currently renders a blank page. */
const BROKEN_UPGRADE_URLS = [
    'https://dashboard.eauditor.app/upgrade',
    'http://dashboard.eauditor.app/upgrade',
]

export const FREE_MAX_SEATS = 10
export const FREE_MAX_ACTIVE_TEMPLATES = 5

export const UNKNOWN_USAGE_SNAPSHOT = {
    companyId: null,
    planId: null,
    planName: null,
    tier: 'Unknown',
    seats: { current: 0, cap: null, isOver: false },
    activeTemplates: { current: 0, cap: null, isOver: false },
    isOverAnyLimit: false,
    upgradeUrl: DEFAULT_UPGRADE_URL,
}

export const isFreeTier = tier => tier === 'Free'

/** Unpaid accounts still get the Free caps even if Usage says Yearly/Premium. */
export const asFreeDisplayUsage = usage => {
    if (!usage) return null
    if (isFreeTier(usage.tier) && usage.seats?.cap != null) return usage
    const seatsCurrent = Number(usage.seats?.current ?? 0)
    const templatesCurrent = Number(usage.activeTemplates?.current ?? 0)
    const seats = {
        current: seatsCurrent,
        cap: FREE_MAX_SEATS,
        isOver: seatsCurrent > FREE_MAX_SEATS,
    }
    const activeTemplates = {
        current: templatesCurrent,
        cap: FREE_MAX_ACTIVE_TEMPLATES,
        isOver: templatesCurrent > FREE_MAX_ACTIVE_TEMPLATES,
    }
    return {
        ...usage,
        planName: 'Free',
        tier: 'Free',
        seats,
        activeTemplates,
        isOverAnyLimit: seats.isOver || activeTemplates.isOver,
    }
}

export const isUnlimitedCap = cap => cap == null

export const getUsageRatio = metric => {
    if (!metric || isUnlimitedCap(metric.cap) || metric.cap <= 0) return null
    return metric.current / metric.cap
}

/** blue < 80% · amber 80-99% · red 100%+ · green unlimited */
export const getBarTone = metric => {
    if (!metric || isUnlimitedCap(metric.cap)) return 'unlim'
    const ratio = metric.current / metric.cap
    if (ratio >= 1) return 'over'
    if (ratio >= 0.8) return 'warn'
    return 'ok'
}

export const getWorstTone = usage => {
    if (!usage || !isFreeTier(usage.tier)) return 'ok'
    const seatsTone = getBarTone(usage.seats)
    const templatesTone = getBarTone(usage.activeTemplates)
    const rank = { ok: 0, warn: 1, over: 2, unlim: -1 }
    return rank[seatsTone] >= rank[templatesTone] ? seatsTone : templatesTone
}

export const barWidthPercent = metric => {
    if (!metric || isUnlimitedCap(metric.cap)) return 100
    if (metric.cap <= 0) return 100
    return Math.min(100, (metric.current / metric.cap) * 100)
}

export const formatUsageLabel = metric => {
    if (!metric) return '—'
    if (isUnlimitedCap(metric.cap)) {
        return { current: metric.current, suffix: 'Unlimited', capped: false }
    }
    return {
        current: metric.current,
        suffix: `/ ${metric.cap}`,
        capped: true,
    }
}

export const formatChipText = usage => {
    if (!usage || !isFreeTier(usage.tier)) return null
    const seats = usage.seats || {}
    const templates = usage.activeTemplates || {}
    return `Free · ${seats.current}/${seats.cap} seats · ${templates.current}/${templates.cap} templates`
}

/** Compact chip text for tablet/mobile header. */
export const formatChipTextCompact = usage => {
    if (!usage || !isFreeTier(usage.tier)) return null
    const seats = usage.seats || {}
    const templates = usage.activeTemplates || {}
    return `Free · ${seats.current}/${seats.cap} · ${templates.current}/${templates.cap}`
}

export const resolveUpgradeUrl = url => {
    if (!url || BROKEN_UPGRADE_URLS.includes(String(url).trim())) {
        return DEFAULT_UPGRADE_URL
    }
    return url
}

/**
 * Backend currently returns planId: 0 + tier: "Unknown" + cap: null for Free
 * orgs (ServicePlan 0). Treat that as Free with pricing-page caps until BE
 * returns tier "Free" and real caps.
 */
export const shouldTreatAsFreePlan = ({ tier, planId, planName }) => {
    if (tier === 'Free' || planName === 'Free') return true
    if (tier === 'Premium' || tier === 'Enterprise') return false
    // planId 0 matches login Companies[].ServicePlan = 0 (Free)
    if (planId === 0 || planId === '0') return true
    return false
}

const buildMetric = (rawMetric, freeCap) => {
    const current = Number(rawMetric.current ?? rawMetric.Current ?? 0)
    let cap
    if (rawMetric.cap !== undefined) {
        cap = rawMetric.cap
    } else if (rawMetric.Cap !== undefined) {
        cap = rawMetric.Cap
    } else {
        cap = null
    }
    if (freeCap != null && cap == null) {
        cap = freeCap
    }
    const isOverFromApi = rawMetric.isOver ?? rawMetric.IsOver
    let isOver = false
    if (typeof isOverFromApi === 'boolean') {
        isOver = isOverFromApi
    } else if (cap != null) {
        isOver = current > cap
    }
    return { current, cap, isOver }
}

export const normalizeUsageSnapshot = raw => {
    if (!raw || typeof raw !== 'object') return { ...UNKNOWN_USAGE_SNAPSHOT }

    const seatsRaw = raw.seats || raw.Seats || {}
    const templatesRaw = raw.activeTemplates || raw.ActiveTemplates || {}
    const planId = raw.planId ?? raw.PlanId ?? null
    const planName = raw.planName ?? raw.PlanName ?? null
    let tier = raw.tier ?? raw.Tier ?? 'Unknown'

    const asFree = shouldTreatAsFreePlan({ tier, planId, planName })
    if (asFree && (tier === 'Unknown' || !tier)) {
        tier = 'Free'
    }

    const seats = buildMetric(seatsRaw, asFree ? FREE_MAX_SEATS : null)
    const activeTemplates = buildMetric(
        templatesRaw,
        asFree ? FREE_MAX_ACTIVE_TEMPLATES : null,
    )

    if (asFree) {
        seats.isOver = seats.current > seats.cap
        activeTemplates.isOver = activeTemplates.current > activeTemplates.cap
    }

    const flaggedOver = Boolean(
        raw.isOverAnyLimit ?? raw.IsOverAnyLimit ?? false,
    )
    const isOverAnyLimit = flaggedOver || seats.isOver || activeTemplates.isOver

    return {
        companyId: raw.companyId ?? raw.CompanyId ?? null,
        planId,
        planName: asFree ? planName || 'Free' : planName,
        tier: asFree ? 'Free' : tier,
        seats,
        activeTemplates,
        isOverAnyLimit,
        upgradeUrl: resolveUpgradeUrl(
            raw.upgradeUrl || raw.UpgradeUrl || DEFAULT_UPGRADE_URL,
        ),
    }
}

export const ACCOUNT_PLAN_PATH = '/app/settings/account-plan'
export const ACCOUNT_PRICING_PATH = '/app/account/pricing'
export const ACCOUNT_CHECKOUT_PATH = '/app/account/checkout'
export const CONTACT_SALES_URL = 'https://eauditor.app/contact-us/'
export const START_TRIAL_URL = 'https://dashboard.eauditor.app/'

/** Login ServicePlan: 0 / null / falsy => Free (matches ProfileDropdown). */
export const isLoginServicePlanFree = user => {
    const plan = user?.Companies?.[0]?.ServicePlan ?? user?.ServicePlan ?? null
    return !plan || plan === 0
}

const flagOn = value =>
    value === true || value === 1 || value === '1' || value === 'true'

const planEndStillOpen = value => {
    if (value == null || value === '') return true
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return true
    return date.getTime() > Date.now()
}

/**
 * PaymentStatus nests the live plan on ServicePlan.
 * IsPaidMember can stay false after Cart/Confirm while IsPaidPlan is true.
 */
export const isPaymentStatusPaid = data => {
    if (!data || typeof data !== 'object') return false
    const nestedRaw = data.ServicePlan ?? data.servicePlan
    const nested =
        nestedRaw && typeof nestedRaw === 'object' ? nestedRaw : null
    const source = nested || data
    const ending =
        source.PlanEndingOn ||
        source.planEndingOn ||
        data.PlanEndingOn ||
        data.planEndingOn
    if (!planEndStillOpen(ending)) return false
    const paidMember =
        source.IsPaidMember ??
        source.isPaidMember ??
        data.IsPaidMember ??
        data.isPaidMember
    const paidPlan =
        source.IsPaidPlan ??
        source.isPaidPlan ??
        data.IsPaidPlan ??
        data.isPaidPlan
    return flagOn(paidMember) || flagOn(paidPlan)
}

/** Plan name from PaymentStatus when the live plan is still paid. */
export const paymentPlanLabel = data => {
    if (!isPaymentStatusPaid(data)) return ''
    const nestedRaw = data.ServicePlan ?? data.servicePlan
    const nested =
        nestedRaw && typeof nestedRaw === 'object' ? nestedRaw : null
    const raw =
        nested?.PlanDescription ||
        nested?.planDescription ||
        nested?.PlanName ||
        nested?.planName ||
        data.PlanName ||
        data.planName ||
        ''
    return String(raw).trim()
}

/** When Usage API is missing (404), still show Free widgets from login plan. */
export const buildProvisionalFreeUsage = () => ({
    companyId: null,
    planId: 0,
    planName: 'Free',
    tier: 'Free',
    seats: { current: 0, cap: FREE_MAX_SEATS, isOver: false },
    activeTemplates: {
        current: 0,
        cap: FREE_MAX_ACTIVE_TEMPLATES,
        isOver: false,
    },
    isOverAnyLimit: false,
    upgradeUrl: DEFAULT_UPGRADE_URL,
    _usageApiUnavailable: true,
})
